import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { notification } from 'ant-design-vue';
import { ProductForm } from '@/interfaces/components/configurator/ProductForm';
import ChecklistSegmentModule from '@/components/views/productView/ChecklistSegmentModule.vue';
import PriceModule from '@/components/views/project/PriceModule.vue';
import { PriceItem } from '@/interfaces/general/PriceItem';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { LoadingOverlayHelper } from '@/helpers/LoadingOverlayHelper';
import { PriceInputObject } from '@/interfaces/components/configurator/PriceInputObject';
import { RouteNames } from '@/enums/routes/RouteNames';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { extractDefaultValues, extractUpdateFunctions } from '@/helpers/FormDataHelper';
import { debounce } from 'vue-debounce';
import { determineFieldVisibility, extractUpdateFunctionValues } from '@/helpers/FieldFunctionHelper';
import { UpdateFunctionEntry } from '@/interfaces/components/configurator/UpdateFunctionEntry';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import JsFunctionRepository from '@/repositories/JsFunctionRepository';
import JsFunction from '@/models/JsFunction';
import { parseAndTransformStringWithDynamicValues, registerJsonLogicFunctions } from '@/helpers/JsonLogicHelper';
import ClientRepository from '@/repositories/ClientRepository';
import { UserRepository } from '@/repositories/UserRepository';
import PopupNotifications from '@/modules/Notifications/components/PopupNotifications.vue';
import { PopupNotificationOptions } from '@/modules/Notifications/interfaces/PopupNotificationOptions';
import { checkForErrorsInFieldsAndReturnState, resetHelperState } from '@/helpers/FormValidationHelper';
import { FormValidationPayload } from '@/interfaces/general/FormValidationPayload.js';
import { ChecklistSegment } from '@/interfaces/components/configurator/ChecklistSegment';
import ColorModule from '@/modules/ColorModule/views/ColorModule.vue';
import store from '@/store';
import router from '@/router';
import i18n from '@/i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'CalculatorProductView',
  emits: [EventBusEvents.calculatorProductData],
  setup(__props, { emit }) {


const { proxy } = getCurrentInstance()!;

const notifications = ref<PopupNotificationOptions[]>([]);
const currentProductForm = ref<ProductForm | null>(null);
const sellingPriceList = ref<PriceItem[]>([]);
const purchasePriceList = ref<PriceItem[]>([]);
const roltekPriceList = ref<PriceItem[]>([]);
const loadingOverlay = new LoadingOverlayHelper(proxy, {});
const activeProductFormId = ref<null | number>(null);
const updateFunctions = ref<UpdateFunctionEntry[]>([]);
const clientId = ref<null | string>(null);
const pIdFieldErrors = ref<string[]>([]);
const hasUnsavedData = ref(false);
const level = ref<number>(2);
const connection = ref<any>(null);
const production = ref(false);
const temporaryDisabledPIDs = ref<string[]>([]);

const translate = (key: string) => i18n.t(key) as string;

const getSingleProductForm = (productFormId: string) =>
    store.dispatch('configurator/getSingleProductForm', productFormId);
const addActiveProductForm = (activeProductForm: ActiveProductFormValueObject): Promise<number | null> =>
    store.dispatch('configurator/addActiveProductForm', activeProductForm);
const productForm = (productFormId: string): ProductForm | null =>
    store.getters['configurator/productForm'](productFormId);
const calculatePrice = (payload: PriceInputObject): Promise<any> =>
    store.dispatch('configurator/calculatePrice', payload);
const resetActiveProductForms = () => store.commit('configurator/resetActiveProductForms');
const updateActiveProductFormValue = (payload: {
    pId: string;
    value: ConfiguratorValue;
    productFormId: number;
}) => store.dispatch('configurator/updateActiveProductFormValue', payload);
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

const userGroup = computed(() => store.getters['jwtData/currentUserGroup']);
const productSystemName = computed(() => router.currentRoute.query.productSystemName as string);
const productDetails = computed(() => {
    if (currentProductForm.value == null) {
        return null;
    }

    return {
        productName: currentProductForm.value.matchcode,
        productCategory: currentProductForm.value.directory,
        productSystemName: productSystemName.value,
    };
});

const client = computed(() => {
    if (clientId.value == null) {
        return null;
    }

    return ClientRepository.getById(clientId.value);
});

const clientsPaymentTypeId = computed(() => {
    if (client.value == null || client.value.paymentType == null) {
        return null;
    }

    return client.value.paymentType.id;
});

const currencySymbol = computed(() => UserRepository.getCurrentUsersCurrencySymbol());
const functions = computed(() => JsFunctionRepository.getAll());
const checklistSegments = computed(() => {
    if (currentProductForm.value == null) {
        return [];
    }

    return currentProductForm.value.checklists.checklistSegments.filter(
        (checklistSegment: ChecklistSegment) => !checklistSegment.isLocked,
    );
});
const productFormValues = computed(() => {
    if (activeProductFormId.value == null) {
        return null;
    }

    return getProductFormValues(activeProductFormId.value);
});
const checklistErrors = computed(() => {
    if (currentProductForm.value == null) {
        return [];
    }

    return currentProductForm.value.checklists.checklistSegments[0].checklistFields.filter(
        (checklistField: ChecklistField) => {
            return (
                (checklistField.fieldType === 'warning' || checklistField.fieldType === 'abort') &&
                productFormValues.value &&
                determineFieldVisibility(checklistField.checklistVisibleFunction, productFormValues.value)
            );
        },
    );
});
const checklistFieldErrorsExist = computed(() => {
    return (
        pIdFieldErrors.value.length > 0 ||
        checklistErrors.value.some((checklistError: any) => checklistError.fieldType === 'abort')
    );
});

const switchLevel = (newLevel: number) => {
    level.value = -1;

    nextTick(() => {
        level.value = newLevel;
    });
};

const triggerVisibilityFunctions = () => {
    EventBus.$emit(EventBusEvents.triggerVisibilityFunctions, {
        activeProductFormValues: productFormValues.value,
    });
};

const triggerOptionsFunctions = () => {
    EventBus.$emit(EventBusEvents.triggerOptionsFunctions, {
        activeProductFormValues: productFormValues.value,
        activeProductFormId: activeProductFormId.value,
    });
};

const goBack = (): void => {
    router.go(-1);
    EventBus.$emit(EventBusEvents.calculatorPurchasePrice, []);
};

const addNotification = (title: string, description: string, errorType: 'error' | 'warning') => {
    notifications.value.push({
        title: translate(title),
        description: translate(description),
        errorType,
    });
};

const calculateConfiguratorPrice = async (values: ActiveProductFormValueObject) => {
    loadingOverlay.start();

    pIdFieldErrors.value = [];
    const offerItemId = router.currentRoute.params.offerItemId;

    if (currentProductForm.value == null) {
        sellingPriceList.value = [];
        return;
    }

    try {
        const prices = await calculatePrice({
            activeProductFormValues: values,
            productFormId: currentProductForm.value.id,
            clientId: clientId.value,
            currencySymbol: currencySymbol.value,
            offerItemId,
            includePurchasePrice: true,
        });

        if (prices.purchasePrices) {
            purchasePriceList.value = prices.purchasePrices;
        }
        roltekPriceList.value = prices.roltekPrices;
        sellingPriceList.value = prices.sellingPrices;
        EventBus.$emit(EventBusEvents.calculatorPurchasePrice, prices);
    } catch (e) {
        if (Array.isArray(e)) {
            addNotification(translate('Error occured'), e[0] as string, 'error');
        }
        loadingOverlay.stop();
    }

    EventBus.$emit(EventBusEvents.calculatorProductData, {
        productFormValues: values,
        connection: connection.value,
        production: production.value,
        checklistFieldErrorsExist: checklistFieldErrorsExist.value,
        paymentTypeId: clientsPaymentTypeId.value,
        productFormId: currentProductForm.value.id,
    });

    loadingOverlay.stop();
};

const clientSelected = (selectedClientId: string) => {
    clientId.value = selectedClientId;
    if (productFormValues.value != null) {
        calculateConfiguratorPrice(productFormValues.value);
    }
};

const getCurrentProductForm = async (): Promise<ProductForm | null> => {
    const productFormId = String(router.currentRoute.params.productFormId);

    try {
        await getSingleProductForm(productFormId);
    } catch (e) {
        addNotification(translate('Error occured'), translate('Error occured'), 'error');

        loadingOverlay.stop();
        router.push({ name: RouteNames.error });
        return null;
    }

    return productForm(productFormId);
};

const updateUnsavedDataState = ({ state }: { state: boolean }) => {
    hasUnsavedData.value = state;
};

const setConfiguratorLevel = () => {
    const expertUserGroups = ['1', '2', '3'];
    const isSmallScreen = window.innerWidth < 640;

    if (expertUserGroups.includes(String(userGroup.value)) && !isSmallScreen) {
        level.value = 2;
    } else {
        level.value = 0;
    }
};

const showErrorState = (description: string) => {
    notification.error({
        message: translate('Invalid characters detected in form fields:'),
        description: description as string,
    });
};

const validateFormFields = () => {
    if (!activeProductFormId.value && activeProductFormId.value !== 0) {
        return;
    }

    // Get visible fields and validate their values
    const formValues: FormValidationPayload[] = [];

    const rowValues = getProductFormValues(activeProductFormId.value);
    const flattenedFields = checklistSegments.value.map((segment) => segment.checklistFields).flat();

    formValues.push({
        rowKey: null,
        values: { ...rowValues },
        fieldSegments: flattenedFields as any,
    });

    const errorState = checkForErrorsInFieldsAndReturnState(formValues);
    const hasErrors = errorState.hasErrors;

    if (hasErrors) {
        showErrorState(errorState.errorDescription);
    }

    EventBus.$emit(EventBusEvents.formValidationFinished, hasErrors);
};

const updateColorValue = (payload: {
    activeProductFormId: number;
    colorSystemPID: string;
    colorCollection: string;
    colorDropdownPID: string;
    selectedColorValue: string | null;
    shouldFreezeManualInput?: boolean;
}) => {
    if (payload.activeProductFormId !== activeProductFormId.value) {
        return;
    }

    updateActiveProductFormValue({
        pId: payload.colorSystemPID,
        value: payload.colorCollection,
        productFormId: payload.activeProductFormId,
    });

    if (payload.selectedColorValue) {
        nextTick(() => {
            updateActiveProductFormValue({
                pId: payload.colorDropdownPID,
                value: payload.selectedColorValue as string,
                productFormId: payload.activeProductFormId,
            });
        });

        // In case we have a selected color value, we should freeze manual input
        if (payload.shouldFreezeManualInput && !temporaryDisabledPIDs.value.includes(payload.colorDropdownPID)) {
            temporaryDisabledPIDs.value.push(payload.colorDropdownPID);
        }

        if (!payload.shouldFreezeManualInput) {
            const index = temporaryDisabledPIDs.value.indexOf(payload.colorDropdownPID);
            if (index > -1) {
                temporaryDisabledPIDs.value.splice(index, 1);
            }
        }
    }
};

const showChecklistErrors = () => {
    notifications.value = [];
    notification.destroy();

    for (const checklistField of checklistErrors.value) {
        window.setTimeout(() => {
            if (checklistField.checklistStringField && checklistField.checklistStringField.defaultValue) {
                switch (checklistField.fieldType) {
                    case 'abort':
                        addNotification(
                            'Error happened',
                            parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue,
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            'error',
                        );

                        break;
                    case 'warning':
                        addNotification(
                            'Warning',
                            parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue,
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            'warning',
                        );
                        break;
                }
            }
        }, 50);
    }
};

const debouncedPriceCalculation = debounce((values: ActiveProductFormValueObject) => {
    calculateConfiguratorPrice(values);
}, 300);
const debounceShowingChecklistErrors = debounce(() => {
    showChecklistErrors();
}, 100);
const debounceTriggerVisibilityFunctions = debounce(() => {
    triggerVisibilityFunctions();
}, 100);
const debounceTriggerOptionsFunctions = debounce(() => {
    triggerOptionsFunctions();
}, 100);

const onProductFormValueChange = (values: ActiveProductFormValueObject) => {
    nextTick(() => {
        updateFunctions.value.forEach((entry: UpdateFunctionEntry) => {
            if (activeProductFormId.value == null) {
                return;
            }
            const updatedValues: ConfiguratorValue[] = extractUpdateFunctionValues(
                entry.updateFunctions,
                entry.type,
                values,
            );
            if (updatedValues.length > 0) {
                updateActiveProductFormValue({
                    pId: entry.id,
                    value: updatedValues[0],
                    productFormId: activeProductFormId.value,
                });
            }
        });

        debounceTriggerVisibilityFunctions();
        debounceTriggerOptionsFunctions();
        // @ts-ignore
        debouncedPriceCalculation(values);
        debounceShowingChecklistErrors();
    });
};

watch(productFormValues as any, onProductFormValueChange, { deep: true, immediate: true });
watch(functions, () => {
    registerJsonLogicFunctions(functions.value);
});

onMounted(async () => {
    resetHelperState();
    clientId.value = null;

    loadingOverlay.start();

    setConfiguratorLevel();
    EventBus.$on(EventBusEvents.changesInDataMade, updateUnsavedDataState);
    EventBus.$on(EventBusEvents.calculatorClientChosen, clientSelected);
    EventBus.$on(EventBusEvents.validateFormFields, validateFormFields);
    EventBus.$on(EventBusEvents.updateSystemColorValue, updateColorValue);

    if (functions.value.length <= 0) {
        try {
            await JsFunction.getAll();
        } catch (e) {
            return e;
        }
    }

    currentProductForm.value = await getCurrentProductForm();

    if (currentProductForm.value == null) {
        return;
    }

    store.dispatch('configurator/determineDefaultPriceList', {
        data: checklistSegments.value,
        dataType: 'checklistSegments',
    });

    activeProductFormId.value = await addActiveProductForm(
        await extractDefaultValues({
            productForm: currentProductForm.value,
            productId: router.currentRoute.params.productId,
            clientId: router.currentRoute.query.clientId as string | null,
            productSystemName: router.currentRoute.query.productSystemName,
        }),
    );

    updateFunctions.value = extractUpdateFunctions(currentProductForm.value);

    emit(EventBusEvents.calculatorProductData, {
        productFormValues: productFormValues.value,
        connection: connection.value,
        production: production.value,
        checklistFieldErrorsExist: checklistFieldErrorsExist.value,
        paymentTypeId: clientsPaymentTypeId.value,
        productFormId: currentProductForm.value.id,
    });

    loadingOverlay.stop();
});

onBeforeUnmount(() => {
    EventBus.$emit(EventBusEvents.calculatorPurchasePrice, []);
    EventBus.$off(EventBusEvents.changesInDataMade, updateUnsavedDataState);
    EventBus.$off(EventBusEvents.calculatorClientChosen, clientSelected);
    EventBus.$off(EventBusEvents.validateFormFields, validateFormFields);
    EventBus.$off(EventBusEvents.updateSystemColorValue, updateColorValue);

    notifications.value = [];
    notification.destroy();
    resetActiveProductForms();
});

return { __sfc: true,emit, proxy, notifications, currentProductForm, sellingPriceList, purchasePriceList, roltekPriceList, loadingOverlay, activeProductFormId, updateFunctions, clientId, pIdFieldErrors, hasUnsavedData, level, connection, production, temporaryDisabledPIDs, translate, getSingleProductForm, addActiveProductForm, productForm, calculatePrice, resetActiveProductForms, updateActiveProductFormValue, getProductFormValues, userGroup, productSystemName, productDetails, client, clientsPaymentTypeId, currencySymbol, functions, checklistSegments, productFormValues, checklistErrors, checklistFieldErrorsExist, switchLevel, triggerVisibilityFunctions, triggerOptionsFunctions, goBack, addNotification, calculateConfiguratorPrice, clientSelected, getCurrentProductForm, updateUnsavedDataState, setConfiguratorLevel, showErrorState, validateFormFields, updateColorValue, showChecklistErrors, debouncedPriceCalculation, debounceShowingChecklistErrors, debounceTriggerVisibilityFunctions, debounceTriggerOptionsFunctions, onProductFormValueChange, ChecklistSegmentModule, PriceModule, PopupNotifications, ColorModule }
}

})